import {
  useAddReferringPhysician,
  useEditReferringPhysician,
} from 'lib/apollo/hooks'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  physicianDetailsFormSchema,
  TNewReferringPhysicianState,
} from './NewReferringPhysician.types'
import {
  formatUSPhoneNumber,
  formatUSPhoneNumberForSubmission,
  getZodError,
} from 'common/utils'
import { useIsMobile } from 'lib/utils/mediaQueries'
import { enqueueSnackbar } from 'notistack'
import { REFERRING_PHYSICIANS } from 'app/ROUTES_CONSTANTS'
import { CustomChangeEvent, ReferringPhysician } from 'common/types'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'

export const SPECIALITY = [
  'Addiction Medicine',
  'Addiction Psychiatry',
  'Adolescent Medicine',
  'Adult Medicine',
  'Aerospace Medicine',
  'Allergy/Immunology',
  'Anesthesiologist',
  'Anesthesiology',
  'Audiology',
  'Cardiac Electrophysiology',
  'Cardiac Surgery',
  'Cardiologist',
  'Cardiology',
  'Certified Registered Nurse Anesthetist',
  'Chiropractic',
  'Clinical Neuropsychology',
  'Clinical Pathology',
  'Clinical Psychology',
  'Clinical Social Worker',
  'Colorectal Surgery',
  'Critical Care',
  'Cytopathology',
  'Dental Hygienist',
  'Dentist',
  'Dermatology',
  'Diagnostic Radiology',
  'Diet/Nutrition',
  'Electrodiagnostic Medicine',
  'Emergency Medicine',
  'Endocrinology',
  'Family Practice',
  'Foot and Ankle Surgery',
  'Forensic Pathology',
  'Forensic Psychiatry',
  'Gastroenterology',
  'GI',
  'General Surgery',
  'Geriatric Medicine',
  'Geriatric Psychiatry',
  'Gynecological Oncology',
  'Hand Surgery',
  'Hematology',
  'Hematology/Oncology',
  'Home Health Aide',
  'Hospice and Palliative Care',
  'Hospitalist',
  'Infectious Disease',
  'Internal Medicine',
  'Interventional Cardiology',
  'Interventional Pain Management',
  'Interventional Radiology',
  'IR',
  'Massage Therapist',
  'Maxillofacial Surgery',
  'Medical Oncology',
  'Neonatal-Perinatal Medicine',
  'Nephrology',
  'Nephrology - Pediatric',
  'Neurology',
  'Neuropsychiatry',
  'Neuroradiology',
  'Neurosurgery',
  'Nuclear Medicine',
  'Nurse',
  'Nurse Practitioner',
  'Obstetrics/Gynecology',
  'Occupational Medicine',
  'Occupational Therapist',
  'Oncology',
  'Ophthalmology',
  'Optometry',
  'Oral and Maxillofacial Surgery',
  'Oral Surgery',
  'OMFS',
  'Orthopedic Spine Surgery',
  'Orthopedic Surgery',
  'Orthopedics',
  'Osteopathic Manipulative Medicine',
  'Otolaryngology',
  'Otolaryngology/Facial Plastic Surgery',
  'Pain Management',
  'Pathology',
  'Pediatric Allergy/Immunology',
  'Pediatric Anesthesiologist',
  'Pediatric Cardiologist',
  'Pediatric Critical Care',
  'Pediatric Dentistry',
  'Pediatric Emergency Medicine',
  'Pediatric Endocrinology',
  'Pediatric Gastroenterology',
  'Pediatric Infectious Disease',
  'Pediatric Medicine',
  'Pediatric Medicine - Developmental & Behavioral',
  'Pediatric Oncology',
  'Pediatric Orthopedic Surgery',
  'Pediatric Otolaryngology',
  'Pediatric Pulmonology',
  'Pediatric Radiology',
  'Pediatric Rheumatology',
  'Pediatric Surgery',
  'Pediatric Urology',
  'Peripheral Vascular Disease',
  'Physical Medicine and Rehabilitation',
  'Physical Therapist',
  'Physician Assistant',
  'Plastic and Reconstructive Surgery',
  'Plastics',
  'Podiatry',
  'Preventive Medicine',
  'Psychiatry',
  'Psychology',
  'Psychosomatic Medicine',
  'Pulmonology',
  'Radiation Oncology',
  'Radiology',
  'Rehabilitation Medicine',
  'Reproductive Endocrinology',
  'Rheumatology',
  'Sleep Medicine',
  'Social Work',
  'Speech Language Pathologist',
  'Sports Medicine',
  'Surgical Oncology',
  'Therapeutic Radiology',
  'Therapist',
  'Therapy',
  'Thoracic Surgery',
  'Trauma Surgery',
  'Urology',
  'Vascular Surgery',
].sort()

export const useNewReferringPhysicianLogic = () => {
  const navigate = useNavigate()

  const isMobile = useIsMobile()

  const state = useLocation().state as TNewReferringPhysicianState
  const { firstName, lastName, email, edit, speciality, mobile, _id } = state
  const { showPhysiciansSpeciality, physiciansTitle, showPhysiciansMobile } =
    useOrganizationSettings()

  const [formData, setFormData] = useState<ReferringPhysician>({
    _id: _id,
    firstName: firstName,
    lastName: lastName,
    email: email,
    confirmEmail: email,
    speciality: speciality,
    mobile: mobile,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>()

  const [editReferringPhysician, { loading: editLoading }] =
    useEditReferringPhysician()

  const [addReferringPhysician, { loading: addLoading }] =
    useAddReferringPhysician()

  const isLoading = edit ? editLoading : addLoading
  const headerTitle = `${physiciansTitle} / ${
    edit
      ? `Edit ${!isMobile ? 'Physician' : ''}`
      : `Add ${!isMobile ? 'Physician' : ''}`
  }`
  const successAlertTitle = `${physiciansTitle} was ${
    edit ? 'updated' : 'created'
  } successfully`

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = physicianDetailsFormSchema.safeParse(formData)
    if (!result.success) {
      const errors = getZodError(result.error)
      setFormErrors(errors)
      return
    }

    const { confirmEmail, mobile, speciality, ...restFormData } = formData

    edit
      ? editReferringPhysician({
          variables: {
            physician: {
              ...restFormData,
              ...(speciality && { speciality }),
              mobile: mobile ? formatUSPhoneNumberForSubmission(mobile) : '',
            },
          },
        }).then((response) => {
          if (response.data?.editPhysician) {
            enqueueSnackbar(successAlertTitle, { variant: 'success' })
          }
          navigate(`/${REFERRING_PHYSICIANS}`)
        })
      : addReferringPhysician({
          variables: {
            physician: {
              ...restFormData,
              ...(speciality && { speciality }),
              ...(mobile && {
                mobile: formatUSPhoneNumberForSubmission(mobile),
              }),
            },
          },
        })
          .then((response) => {
            if (response.data?.createPhysician) {
              enqueueSnackbar(successAlertTitle, { variant: 'success' })
            }
            navigate(`/${REFERRING_PHYSICIANS}`)
          })
          .catch((error) => {
            enqueueSnackbar(error.message, { variant: 'error' })
          })
  }

  const handleChangeUpdatedPhysicianDetails = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | CustomChangeEvent
  ) => {
    if (e.target.name === 'mobile' && typeof e.target.value === 'string') {
      e.target.value = formatUSPhoneNumber(e.target.value.replace(/\D/g, ''))
    }
    if (
      (e.target.name === 'email' || e.target.name === 'confirmEmail') &&
      typeof e.target.value === 'string'
    ) {
      e.target.value = e.target.value.toLowerCase()
    }
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return {
    formData,
    isLoading,
    formErrors,
    headerTitle,
    showPhysiciansMobile,
    showPhysiciansSpeciality,
    navigate,
    handleFormSubmit,
    handleChangeUpdatedPhysicianDetails,
  }
}
